/* Odvození cíle „zvoneček → diář" — na který den skočit a kterou položku zvýraznit.
 *
 * Sdílené mezi:
 *   • NotificationCenter (zvoneček) — z úkolu staví navigační payload (query string),
 *   • DiarPageV3 (diář)            — payload čte a přepne den + pohled + focus.
 *
 * Root cause, který to řeší: dřív `navigate("/diar")` bez cíle → diář ukázal dnešek,
 * ne položku, na kterou poradce klikl (úkol po termínu v minulém týdnu byl mimo obraz).
 *
 * Čistá logika (žádný React / axios / framer) → jednotkově testovatelná bez těžkého
 * renderu. Pravidlo konzistence: `focusDate` se počítá STEJNĚ, jako diář řadí položky
 * do dnů (parseStart → new Date(iso), pak lokální rok/měsíc/den). Tím máme jistotu, že
 * položka padne přesně na den, na který navigujeme (i pro date-only „YYYY-MM-DD").
 */

const VALID_VIEWS = ["day", "week", "month", "agenda"];

// YYYY-MM-DD v LOKÁLNÍM čase (jako `ymd()` v DiarPageV3 — ne UTC/ISO slice).
function localYmd(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/**
 * Z úkolu/notifikace odvodí { focusDate:"YYYY-MM-DD", focusTaskId, view }.
 * `null`, když z položky nejde určit den (chybí/nevalidní termín) → volající pak
 * naviguje na holý „/diar" (dnešek) místo rozbitého odkazu.
 */
export function deriveDiarFocus(task) {
  if (!task || typeof task !== "object") return null;
  const iso = task.deadline || task.start_iso;
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  return {
    focusDate: localYmd(d),
    // id může být 0 nebo číslo → String; prázdné/undefined/null → bez focusu položky.
    focusTaskId: task.id != null && task.id !== "" ? String(task.id) : null,
    // Denní pohled = konkrétní položka je rovnou vidět (funguje na desktopu i mobilu,
    // kde je týdenní mřížka nepoužitelná).
    view: "day",
  };
}

/**
 * Sestaví query string „?date=&task=&view=" pro navigate(`/diar${...}`).
 * `null`, když z úkolu nejde odvodit cíl → volající naviguje na „/diar".
 */
export function diarFocusSearch(task) {
  const f = deriveDiarFocus(task);
  if (!f) return null;
  const p = new URLSearchParams();
  p.set("date", f.focusDate);
  if (f.focusTaskId) p.set("task", f.focusTaskId);
  p.set("view", f.view);
  return `?${p.toString()}`;
}

/**
 * Přečte focus parametry z URLSearchParams (v diáři). Vrací { date:Date|null,
 * focusTaskId, view } nebo `null`, když žádný validní cíl není (běžné otevření /diar).
 * Nevaliduje-li datum formát YYYY-MM-DD, `date` je null (diář zůstane na dnešku),
 * ale focus položky (task) se stále může uplatnit → žádný rozbitý stav.
 */
export function readDiarFocusParams(sp) {
  if (!sp || typeof sp.get !== "function") return null;
  const dateStr = sp.get("date");
  const taskStr = sp.get("task");
  const viewStr = sp.get("view");
  const hasDate = typeof dateStr === "string" && /^\d{4}-\d{2}-\d{2}$/.test(dateStr);
  if (!hasDate && !taskStr) return null;
  let date = null;
  if (hasDate) {
    const [y, m, dd] = dateStr.split("-").map(Number);
    const cand = new Date(y, m - 1, dd);
    // Round-trip: `new Date` normalizuje přetečení (2026-13-99 → 2027-04-08), takže
    // regex na tvar nestačí — ověříme, že se komponenty vrátily beze změny.
    if (!isNaN(cand.getTime()) && cand.getFullYear() === y && cand.getMonth() === m - 1 && cand.getDate() === dd) {
      date = cand;
    }
  }
  return {
    date,
    focusTaskId: taskStr ? String(taskStr) : null,
    view: VALID_VIEWS.includes(viewStr) ? viewStr : "day",
  };
}

/** Shoda položky diáře s aktivním focusem (id porovnané jako string, bez pádu na null). */
export function isFocusedItem(id, focusId) {
  if (id == null || focusId == null) return false;
  return String(id) === String(focusId);
}
